<template>
  <img
    class="play-img"
    :class="[
      { 'play-img--fluid': fluid || fluidGrow, 'play-img--fluid-grow': fluidGrow, 'play-img--block': block },
      roundedClass,
    ]"
    :src="src"
    :alt="alt"
    :width="width || null"
    :height="height || null"
    :loading="lazy ? 'lazy' : null"
    :decoding="lazy ? 'async' : null"
    v-bind="$attrs"
    v-on="$listeners"
  >
  <!-- VUE3-MIGRATION: $listeners removed in Vue 3 (merged into $attrs).
     On migration delete `v-on="$listeners"` — v-bind="$attrs" forwards listeners too. -->
</template>

<script>
/**
 * PlayImg — image element for the Play system.
 *
 * Replaces bootstrap-vue's <b-img> and <b-img-lazy>. `fluid` caps the width to
 * the container (height auto); `fluidGrow` also lets it scale up. `rounded`
 * takes a boolean or a shape ('sm'|'lg'|'circle'|'top'|'bottom'). `lazy` uses
 * the native `loading="lazy"` (no IntersectionObserver, Vue 3-safe) — a lean
 * stand-in for <b-img-lazy>.
 *
 * VUE3-MIGRATION: `v-on="$listeners"` is the only Vue 2-ism; drop it on Vue 3
 * (v-bind="$attrs" keeps forwarding listeners). Props are Vue 3-safe.
 */
export default {
  name: 'PlayImg',
  inheritAttrs: false,
  props: {
    src: {
      type: String,
      default: '',
    },
    alt: {
      type: String,
      default: '',
    },
    width: {
      type: [String, Number],
      default: '',
    },
    height: {
      type: [String, Number],
      default: '',
    },
    /** max-width: 100%; height: auto */
    fluid: {
      type: Boolean,
      default: false,
    },
    /** fluid, but may also scale beyond its natural width */
    fluidGrow: {
      type: Boolean,
      default: false,
    },
    /** true | 'sm' | 'lg' | 'circle' | 'top' | 'bottom' */
    rounded: {
      type: [Boolean, String],
      default: false,
    },
    block: {
      type: Boolean,
      default: false,
    },
    /** Native lazy loading */
    lazy: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    roundedClass() {
      if (this.rounded === true) return 'play-img--rounded'
      if (typeof this.rounded === 'string' && this.rounded) return `play-img--rounded-${this.rounded}`
      return null
    },
  },
}
</script>

<style lang="scss" scoped>
.play-img {
  vertical-align: middle;

  &--fluid { max-width: 100%; height: auto; }
  &--fluid-grow { width: 100%; }
  &--block { display: block; }

  /* ── Rounding ── */
  &--rounded        { border-radius: var(--radius); }
  &--rounded-sm     { border-radius: var(--radius-sm); }
  &--rounded-lg     { border-radius: var(--radius-lg); }
  &--rounded-circle { border-radius: 50%; }
  &--rounded-top    { border-radius: var(--radius) var(--radius) 0 0; }
  &--rounded-bottom { border-radius: 0 0 var(--radius) var(--radius); }
}
</style>
